"use client";

import type { Recipe } from "@/lib/types";

export interface SearchFilters {
  labels?: string[];
  prepTime?: string;
  cookTime?: string;
  ingredients?: string[];
  creatorUid?: string;
  isFavorite?: boolean;
}

export interface SearchOptions {
  scope?: "box" | "all"; // Search within a box or across all boxes
  recipeBoxId?: string; // Required if scope is "box"
  limit?: number; // Maximum number of results
}

/**
 * Normalize text for searching (lowercase, trim)
 */
function normalizeSearchText(text: string): string {
  return text.toLowerCase().trim();
}

/**
 * Check if a search term matches text
 */
function matchesSearchTerm(text: string, searchTerm: string): boolean {
  const normalizedText = normalizeSearchText(text);
  const normalizedTerm = normalizeSearchText(searchTerm);
  return normalizedText.includes(normalizedTerm);
}

/**
 * Check if any search term matches text
 */
function matchesAnySearchTerm(text: string, searchTerms: string[]): boolean {
  return searchTerms.some((term) => matchesSearchTerm(text, term));
}

/**
 * Highlight matching text in a string
 */
export function highlightMatch(text: string, searchTerm: string): string {
  if (!searchTerm.trim()) {
    return text;
  }

  const normalizedText = normalizeSearchText(text);
  const normalizedTerm = normalizeSearchText(searchTerm);
  const index = normalizedText.indexOf(normalizedTerm);

  if (index === -1) {
    return text;
  }

  // Find the actual match in the original text (preserving case)
  const actualMatch = text.substring(index, index + searchTerm.length);
  return text.replace(
    new RegExp(actualMatch.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi"),
    `<mark class="bg-yellow-200 dark:bg-yellow-800">$&</mark>`,
  );
}

/**
 * Search recipes by query string
 */
export function searchRecipes(
  recipes: Recipe[],
  query: string,
  filters?: SearchFilters,
  options?: SearchOptions,
): Recipe[] {
  let results = [...recipes];

  // Apply scope filter
  if (options?.scope === "box" && options.recipeBoxId) {
    results = results.filter((recipe) => recipe.recipeBoxId === options.recipeBoxId);
  }

  // Apply text search
  if (query.trim()) {
    const searchTerms = query
      .split(/\s+/)
      .map((term) => term.trim())
      .filter(Boolean);

    results = results.filter((recipe) => {
      // Search in title
      const titleMatch = matchesAnySearchTerm(recipe.title, searchTerms);

      // Search in labels
      const tagsMatch = recipe.labels.some((label) => matchesAnySearchTerm(label, searchTerms));

      // Search in ingredients
      const ingredientsMatch = recipe.ingredients.some((ingredient) =>
        matchesAnySearchTerm(ingredient, searchTerms),
      );

      // Search in directions
      const directionsMatch = matchesAnySearchTerm(recipe.directions, searchTerms);

      // Search in notes
      const notesMatch = recipe.notes ? matchesAnySearchTerm(recipe.notes, searchTerms) : false;

      return titleMatch || tagsMatch || ingredientsMatch || directionsMatch || notesMatch;
    });
  }

  // Apply filters
  if (filters) {
    if (filters.labels && filters.labels.length > 0) {
      results = results.filter((recipe) =>
        filters.labels!.some((filterLabel) => recipe.labels.includes(filterLabel)),
      );
    }

    if (filters.isFavorite !== undefined) {
      results = results.filter((recipe) => recipe.isFavorite === filters.isFavorite);
    }

    if (filters.creatorUid) {
      results = results.filter((recipe) => recipe.ownerUid === filters.creatorUid);
    }

    if (filters.ingredients && filters.ingredients.length > 0) {
      results = results.filter((recipe) =>
        filters.ingredients!.some((filterIngredient) =>
          recipe.ingredients.some((ingredient) =>
            matchesSearchTerm(ingredient, filterIngredient),
          ),
        ),
      );
    }

    // Note: Prep/cook time filtering would require parsing time strings
    // This is a simplified version - you might want to enhance this
    if (filters.prepTime) {
      results = results.filter((recipe) =>
        recipe.prepTime ? matchesSearchTerm(recipe.prepTime, filters.prepTime!) : false,
      );
    }

    if (filters.cookTime) {
      results = results.filter((recipe) =>
        recipe.cookTime ? matchesSearchTerm(recipe.cookTime, filters.cookTime!) : false,
      );
    }
  }

  // Apply limit
  if (options?.limit && options.limit > 0) {
    results = results.slice(0, options.limit);
  }

  return results;
}

/**
 * Get unique labels from recipes
 */
export function getUniqueLabels(recipes: Recipe[]): string[] {
  const labelSet = new Set<string>();
  recipes.forEach((recipe) => {
    recipe.labels.forEach((label) => labelSet.add(label));
  });
  return Array.from(labelSet).sort();
}

// Legacy alias
export const getUniqueTags = getUniqueLabels;

/**
 * Get unique ingredients from recipes
 */
export function getUniqueIngredients(recipes: Recipe[]): string[] {
  const ingredientSet = new Set<string>();
  recipes.forEach((recipe) => {
    recipe.ingredients.forEach((ingredient) => {
      // Extract base ingredient name (before any quantities or measurements)
      const baseIngredient = ingredient
        .split(/\s+/)
        .slice(-2)
        .join(" ")
        .toLowerCase();
      if (baseIngredient) {
        ingredientSet.add(baseIngredient);
      }
    });
  });
  return Array.from(ingredientSet).sort();
}

/**
 * Get unique creators from recipes
 */
export function getUniqueCreators(recipes: Recipe[]): string[] {
  const creatorSet = new Set<string>();
  recipes.forEach((recipe) => {
    if (recipe.ownerUid) {
      creatorSet.add(recipe.ownerUid);
    }
  });
  return Array.from(creatorSet);
}

